import { useEffect } from 'react'
import { useAppSelector } from 'hooks/reduxHooks'
import { sum } from 'lib/array/arrayUtils'
import calculateTierRemaining from 'loyalty/lib/calculateTierRemaining'
import determineNextLoyaltyTier from 'loyalty/lib/determineNextLoyaltyTier'
import { getTierProgressionRetainModeState } from 'loyalty/lib/tierProgressionRetainModeState'
import { getTierOrder, getTierRank } from 'loyalty/lib/tierUtils'
import { getLastSeenBalance, setLastSeenBalance } from 'loyalty/storage/loyaltyRewardsNotification'
import useCurrentLoyaltyTier from 'loyalty/hooks/useCurrentLoyaltyTier'
import getLoyaltyTierThresholds, { getTierThreshold } from 'loyalty/selectors/getLoyaltyTierThresholds'
import { getMembershipYearContext } from 'loyalty/lib/membershipYearUtils'

export interface LoyaltyRewardsConfig {
  previousPoints: number
  currentPoints: number
  previousStatusCredits: number
  currentStatusCredits: number
  previousNights: number
  currentNights: number
  tier: App.LoyaltyTier
  creditsRemaining: number
  nightsRemaining: number
  /** The date the user's tier is reviewed (statusReviewDate). Used in the platinum-retain congratulations banner. */
  tierReviewDate?: string
  /** The end of the user's current membership year. Used as the "by" date in the unlock card. */
  membershipYearEndDate?: string
  /**
   * For retain mode only: which membership year contains the tier review.
   * - `'current-year'`: tier review at end of current membership year. Calculated remaining (bolded).
   * - `'next-year'`: member upgraded mid-year and the tier review is in the *next* membership year. Absolute requirements (not bolded).
   */
  retainVariant?: 'current-year' | 'next-year'
  isRetain: boolean
  unlockCardTier: App.LoyaltyTier
  targetCredits: number
  targetNights: number
  firstName?: string
  pendingStatusCredits: number
  pendingNights: number
  isUpgrade?: boolean
  newTier?: App.LoyaltyTier
  isPlatinumRetainCelebration?: boolean
}

export function showLoyaltyRewardsNotification(config: LoyaltyRewardsConfig) {
  document.dispatchEvent(new CustomEvent<LoyaltyRewardsConfig>('loyalty-rewards-notification', {
    detail: config,
  }))
}

export function closeLoyaltyRewardsNotification() {
  document.dispatchEvent(new CustomEvent('close-loyalty-rewards-notification'))
}

function useLoyaltyRewardsNotification() {
  const loyaltyAccount = useAppSelector(state => state.loyalty.account)
  const pendingPointsData = useAppSelector(state => state.loyalty.accountPendingPoints.data)
  const firstName = useAppSelector(state => state.auth.account?.givenName)
  const [loyaltyTier] = useCurrentLoyaltyTier()
  const tierThresholds = useAppSelector(getLoyaltyTierThresholds)

  useEffect(() => {
    if (loyaltyAccount.fetching || loyaltyAccount.initial) return
    if (!loyaltyAccount.data) return

    const account = loyaltyAccount.data
    const memberId = `${account.accountNumber}`

    const currentBalance = {
      points: account.points,
      statusCredits: account.statusCredits,
      nights: account.numberOfNights,
      tier: account.tier,
    }

    const lastSeen = getLastSeenBalance(memberId)

    if (!lastSeen) {
      setLastSeenBalance(memberId, currentBalance)
      return
    }

    // Invite-only tiers (e.g. 'infinite') sit outside the earnable progression — never show an
    // "unlocked {tier}" upgrade or a "reach next tier" progress notification for them.
    if (!getTierOrder().includes(account.tier)) {
      setLastSeenBalance(memberId, currentBalance)
      return
    }

    const pointsIncreased = currentBalance.points > lastSeen.points
    const creditsIncreased = currentBalance.statusCredits > lastSeen.statusCredits
    const nightsIncreased = currentBalance.nights > lastSeen.nights
    const lastSeenTierRank = getTierRank(lastSeen.tier)
    const tierUpgraded = getTierRank(account.tier) > lastSeenTierRank && lastSeenTierRank >= 0

    if (!pointsIncreased && !creditsIncreased && !nightsIncreased && !tierUpgraded) {
      setLastSeenBalance(memberId, currentBalance)
      return
    }

    /** Denominators need config fallbacks; without `tierThresholds` retain/upgrade targets can be wrong. */
    if (!tierUpgraded && !tierThresholds) {
      return
    }

    const pendingStatusCredits = sum(pendingPointsData ?? [], p => p.statusCredits)
    const pendingNights = sum(pendingPointsData ?? [], p => p.numNights ?? 0)

    const membershipYear = getMembershipYearContext(account.statusReviewDate, account.membershipYearEndDate, account.createdAt)
    const membershipYearEndDate = membershipYear?.end.toISOString()
    const retainVariant = membershipYear?.retainVariant ?? 'current-year'

    if (tierUpgraded) {
      if (!tierThresholds) {
        /** Wait for config: persisting lastSeen here drops tier-only upgrades when thresholds load later. */
        return
      }
      const unlockedThreshold = getTierThreshold(tierThresholds, account.tier)
      const targetCredits =
        unlockedThreshold?.statusCredits ?? account.totalCreditsForTierRetention ?? 0
      const targetNights =
        unlockedThreshold?.nights ?? account.numberOfNightsForTierRetention ?? 0
      const statusCredits = account.statusCredits ?? 0
      const numberOfNights = account.numberOfNights ?? 0
      const creditsRemaining = Math.max(0, targetCredits - statusCredits)
      const nightsRemaining = Math.max(0, targetNights - numberOfNights)
      showLoyaltyRewardsNotification({
        previousPoints: lastSeen.points,
        currentPoints: currentBalance.points,
        previousStatusCredits: lastSeen.statusCredits,
        currentStatusCredits: currentBalance.statusCredits,
        previousNights: lastSeen.nights,
        currentNights: currentBalance.nights,
        tier: lastSeen.tier,
        newTier: account.tier,
        isUpgrade: true,
        targetCredits,
        targetNights,
        creditsRemaining,
        nightsRemaining,
        tierReviewDate: account.statusReviewDate,
        membershipYearEndDate,
        retainVariant,
        isRetain: false,
        unlockCardTier: account.tier,
        firstName,
        pendingStatusCredits,
        pendingNights,
      })
      setLastSeenBalance(memberId, currentBalance)
      return
    }

    if (!tierThresholds) {
      return
    }

    const nextTier = determineNextLoyaltyTier(account.tier)
    const { isRetainMode } = getTierProgressionRetainModeState(account.tier, nextTier, account, tierThresholds)
    const progressionMode: 'upgrade' | 'retain' = isRetainMode ? 'retain' : 'upgrade'

    const { creditsRemaining, nightsRemaining, targetCredits, targetNights } = calculateTierRemaining(
      account,
      progressionMode,
      tierThresholds,
    )
    const isRetain = isRetainMode
    const unlockCardTier = isRetainMode ? (loyaltyTier ?? account.tier) : (nextTier ?? loyaltyTier ?? account.tier)

    const isPlatinumRetainCelebration =
      account.tier === 'platinum' &&
      isRetain &&
      creditsRemaining === 0 &&
      nightsRemaining === 0

    showLoyaltyRewardsNotification({
      previousPoints: lastSeen.points,
      currentPoints: currentBalance.points,
      previousStatusCredits: lastSeen.statusCredits,
      currentStatusCredits: currentBalance.statusCredits,
      previousNights: lastSeen.nights,
      currentNights: currentBalance.nights,
      tier: account.tier,
      creditsRemaining,
      nightsRemaining,
      tierReviewDate: account.statusReviewDate,
      membershipYearEndDate,
      retainVariant,
      isRetain,
      unlockCardTier,
      targetCredits,
      targetNights,
      firstName,
      pendingStatusCredits,
      pendingNights,
      isPlatinumRetainCelebration,
    })
    setLastSeenBalance(memberId, currentBalance)
  }, [loyaltyAccount, loyaltyTier, pendingPointsData, firstName, tierThresholds])
}

export default useLoyaltyRewardsNotification
